import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import {
  IAdminPatientDetail,
  IPatientAction,
  IPatientSurveyWithAnswers,
} from "../../../../@types/admin/AdminPatient";
import { TableSorted } from "../../../../components/TableSorted";
import {
  usePatientActions,
  usePatientSurveys,
  usePatientTriggeredAlerts,
} from "../../../../hooks/queries/admin/patients";
import { useModal } from "../../../../hooks/useModal";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import { humanizeEnumName } from "../../../../utils/enumNames";
import {
  ErrorMessage,
  FilterButton,
  SectionCard,
  SectionHeader,
  SectionTitle,
  SectionTitleGroup,
} from "../AdminPage.styles";
import { AlertsPanel } from "../AlertsPanel";
import { PageInfoTooltip } from "../PageInfoTooltip";
import { PatientDevicesPanel } from "../PatientDevicesPanel";
import { PatientTreatmentsPanel } from "../PatientTreatmentsPanel";
import { TableContainer } from "../TableContainer";
import { TablePagination } from "../TablePagination";
import { TableLoadingState } from "../TableStates";
import { TableWrapper } from "../TableWrapper";
import { useMockPagination } from "../useMockPagination";
import { WorkflowsPanel } from "../WorkflowsPanel";
import { PanelStack } from "./PatientDetailsPanel.styles";
import { PatientSurveyAnswersModal } from "./PatientSurveyAnswersModal";
import {
  TRIGGERED_ALERT_SEARCH_FIELDS,
  useTriggeredAlertColumns,
} from "./triggeredAlertColumns";

interface IPatientMonitoringTabProps {
  patient: IAdminPatientDetail;
}

/**
 * Monitoring tab of the patient detail panel (EN-5170): what the platform is
 * doing for this patient and what it has done.
 *
 * Almost all composition. Six of the eight panels Retool embedded here already
 * shipped as shared components with their own tickets, so this tab renders them
 * with `patientId` rather than re-implementing anything:
 *
 *   moduleWorkflows          -> WorkflowsPanel        (EN-5167)
 *   moduleAlerts             -> AlertsPanel           (EN-5165)
 *   moduleDevices            -> PatientDevicesPanel   (EN-5166)
 *   modulePatientTreatments  -> PatientTreatmentsPanel(EN-5156)
 *
 * The two that had no shared component are here: protocol actions (which was
 * part 1's `getPatientActionList`) and `modulePatientAlerts`.
 *
 * ⚠️ `moduleAlerts` and `modulePatientAlerts` are different modules and the
 * ticket lists both. `moduleAlerts` is the alert CONFIGURATIONS — the rules set
 * to fire. `modulePatientAlerts` is the alerts that HAVE fired. Both belong
 * here and they are not interchangeable.
 */
export function PatientMonitoringTab({ patient }: IPatientMonitoringTabProps) {
  const patientId = Number(patient.id);
  const { openModal } = useModal();

  const {
    actions,
    isLoading: actionsLoading,
    isError: actionsError,
  } = usePatientActions(patientId);
  const {
    triggeredAlerts,
    isLoading: triggeredLoading,
    isError: triggeredError,
  } = usePatientTriggeredAlerts(patientId);
  const {
    surveys,
    isLoading: surveysLoading,
    isError: surveysError,
  } = usePatientSurveys(patientId);

  const actionsPagination = useMockPagination({
    data: actions,
    searchFields: ["name", "actionType", "frequency"],
  });
  const triggeredPagination = useMockPagination({
    data: triggeredAlerts,
    searchFields: TRIGGERED_ALERT_SEARCH_FIELDS,
  });
  const surveysPagination = useMockPagination({
    data: surveys,
    searchFields: ["surveyName"],
  });

  const actionColumns: ColumnDef<IPatientAction, string>[] = useMemo(
    () => [
      { accessorKey: "name", header: "Action", enableSorting: true },
      {
        id: "actionType",
        header: "Type",
        accessorFn: (action) => humanizeEnumName(action.actionType) || "-",
        enableSorting: true,
      },
      { accessorKey: "actionValue", header: "Value", enableSorting: true },
      { accessorKey: "frequency", header: "Frequency", enableSorting: true },
      {
        accessorKey: "startDate",
        header: "Start",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.startDate, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "lastCompleted",
        header: "Last completed",
        cell: ({ row }) => (
          <span>
            {getLongFormattedDate(row.original.lastCompleted, "Never")}
          </span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "nextActionDate",
        header: "Next due",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.nextActionDate, "-")}</span>
        ),
        enableSorting: true,
      },
    ],
    []
  );

  const triggeredColumns = useTriggeredAlertColumns();

  const surveyColumns: ColumnDef<IPatientSurveyWithAnswers, string>[] = useMemo(
    () => [
      { accessorKey: "surveyName", header: "Survey", enableSorting: true },
      {
        accessorKey: "lastCompletedDate",
        header: "Last completed",
        cell: ({ row }) => (
          <span>
            {getLongFormattedDate(row.original.lastCompletedDate, "-")}
          </span>
        ),
        enableSorting: true,
      },
      {
        // The score can legitimately be 0, which TableSorted would dash, so the
        // accessor renders it as text.
        id: "lastTotalScore",
        header: "Last total score",
        accessorFn: (survey) =>
          survey.lastTotalScore === null || survey.lastTotalScore === undefined
            ? "-"
            : String(survey.lastTotalScore),
        enableSorting: true,
      },
      {
        id: "answerCount",
        header: "Answers",
        accessorFn: (survey) => String((survey.questionsAnswers ?? []).length),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <FilterButton
            variant="secondary"
            disabled={(row.original.questionsAnswers ?? []).length === 0}
            onClick={(e) => {
              e.stopPropagation();
              openModal(<PatientSurveyAnswersModal survey={row.original} />, {
                width: "min(760px, 94vw)",
                maxHeight: "90vh",
                showCloseButton: true,
              });
            }}
          >
            View answers
          </FilterButton>
        ),
      },
    ],
    []
  );

  return (
    <PanelStack>
      <SectionCard>
        <SectionHeader>
          <SectionTitleGroup>
            <SectionTitle>Protocol actions</SectionTitle>
            <PageInfoTooltip title="Protocol actions">
              What this patient is asked to record
            </PageInfoTooltip>
          </SectionTitleGroup>
        </SectionHeader>

        {actionsError && (
          <ErrorMessage>
            Failed to load this patient&apos;s protocol actions.
          </ErrorMessage>
        )}

        <TableContainer>
          <TableWrapper refreshKeys={[["admin-patient-actions"]]}>
            <TableSorted
              columns={actionColumns}
              data={actionsPagination.items}
              emptyStateText={
                actionsLoading ? <TableLoadingState /> : "No protocol actions"
              }
            />
          </TableWrapper>
          <TablePagination
            pagination={actionsPagination.pagination}
            onPageChange={actionsPagination.setPage}
            onPageSizeChange={actionsPagination.setPageSize}
            exportCsv={{
              filename: `patient-${patientId}-protocol-actions`,
              columns: actionColumns,
              rows: actionsPagination.allItems,
            }}
          />
        </TableContainer>
      </SectionCard>

      <SectionCard>
        <SectionHeader>
          <SectionTitleGroup>
            <SectionTitle>Triggered alerts</SectionTitle>
            <PageInfoTooltip title="Triggered alerts">
              Alerts that have fired for this patient
            </PageInfoTooltip>
          </SectionTitleGroup>
        </SectionHeader>

        {triggeredError && (
          <ErrorMessage>
            Failed to load this patient&apos;s triggered alerts.
          </ErrorMessage>
        )}

        <TableContainer>
          <TableWrapper refreshKeys={[["admin-patient-triggered-alerts"]]}>
            <TableSorted
              columns={triggeredColumns}
              data={triggeredPagination.items}
              emptyStateText={
                triggeredLoading ? (
                  <TableLoadingState />
                ) : (
                  "No alerts have fired for this patient"
                )
              }
            />
          </TableWrapper>
          <TablePagination
            pagination={triggeredPagination.pagination}
            onPageChange={triggeredPagination.setPage}
            onPageSizeChange={triggeredPagination.setPageSize}
            exportCsv={{
              filename: `patient-${patientId}-triggered-alerts`,
              columns: triggeredColumns,
              rows: triggeredPagination.allItems,
            }}
          />
        </TableContainer>
      </SectionCard>

      {/* Configured alerts — the rules, not the firings above. */}
      <SectionCard>
        <AlertsPanel patientId={patientId} title="Configured alerts" />
      </SectionCard>

      <SectionCard>
        <WorkflowsPanel patientId={patientId} title="Workflows" />
      </SectionCard>

      <SectionCard>
        <SectionHeader>
          <SectionTitleGroup>
            <SectionTitle>Surveys</SectionTitle>
            <PageInfoTooltip title="Surveys">
              Completed surveys and their answers
            </PageInfoTooltip>
          </SectionTitleGroup>
        </SectionHeader>

        {surveysError && (
          <ErrorMessage>
            Failed to load this patient&apos;s surveys.
          </ErrorMessage>
        )}

        <TableContainer>
          <TableWrapper refreshKeys={[["admin-patient-surveys"]]}>
            <TableSorted
              columns={surveyColumns}
              data={surveysPagination.items}
              emptyStateText={
                surveysLoading ? <TableLoadingState /> : "No completed surveys"
              }
            />
          </TableWrapper>
          <TablePagination
            pagination={surveysPagination.pagination}
            onPageChange={surveysPagination.setPage}
            onPageSizeChange={surveysPagination.setPageSize}
            exportCsv={{
              filename: `patient-${patientId}-surveys`,
              columns: surveyColumns,
              rows: surveysPagination.allItems,
            }}
          />
        </TableContainer>
      </SectionCard>

      <SectionCard>
        <PatientDevicesPanel patientId={patientId} title="Devices" />
      </SectionCard>

      <SectionCard>
        <PatientTreatmentsPanel patientId={patientId} title="Treatments" />
      </SectionCard>
    </PanelStack>
  );
}
